/**
 * エディタ表示設定（文字サイズ・行間・字間）の読み書き・永続化を行う composable。
 */
import { ref, computed, Ref, ComputedRef } from "vue";

const STORAGE_KEY = "editorStyleSettings";
const CURRENT_VERSION = 1;

export interface EditorStyleSettings {
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
}

const DEFAULT_SETTINGS: EditorStyleSettings = {
  fontSize: 12.0,
  lineHeight: 1.8,
  letterSpacing: 0.0,
};

const FONT_SIZE_MIN = 8.0;
const FONT_SIZE_MAX = 26.0;
const LINE_HEIGHT_MIN = 1.0;
const LINE_HEIGHT_MAX = 3.0;
const LETTER_SPACING_MIN = -1.0;
const LETTER_SPACING_MAX = 5.0;

/** Number変換し、NaN の場合は fallback を返す */
function safeNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clampSettings(raw: Partial<EditorStyleSettings>): EditorStyleSettings {
  return {
    fontSize: Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, safeNumber(raw.fontSize, DEFAULT_SETTINGS.fontSize))),
    lineHeight: Math.min(
      LINE_HEIGHT_MAX,
      Math.max(LINE_HEIGHT_MIN, safeNumber(raw.lineHeight, DEFAULT_SETTINGS.lineHeight))
    ),
    letterSpacing: Math.min(
      LETTER_SPACING_MAX,
      Math.max(LETTER_SPACING_MIN, safeNumber(raw.letterSpacing, DEFAULT_SETTINGS.letterSpacing))
    ),
  };
}

function loadFromStorage(): EditorStyleSettings {
  if (typeof window === "undefined" || !window.localStorage) {
    return { ...DEFAULT_SETTINGS };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as { value?: Partial<EditorStyleSettings>; version?: number };
    const value = parsed?.value ?? parsed;
    if (!value || typeof value !== "object") return { ...DEFAULT_SETTINGS };
    return clampSettings({ ...DEFAULT_SETTINGS, ...value });
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveToStorage(settings: EditorStyleSettings): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ value: settings, version: CURRENT_VERSION }));
  } catch {
    // ignore
  }
}

export interface UseEditorStyleSettingsReturn {
  settings: Ref<EditorStyleSettings>;
  editorStyle: ComputedRef<Record<string, string>>;
  loadSettings: () => EditorStyleSettings;
  saveSettings: (newSettings: EditorStyleSettings) => void;
  resetToDefault: () => void;
  defaults: EditorStyleSettings;
  limits: {
    fontSize: { min: number; max: number };
    lineHeight: { min: number; max: number };
    letterSpacing: { min: number; max: number };
  };
}

// モーダルとエディタで同一の設定を共有するため、ref はモジュールで1つだけ持つ
let sharedSettings: Ref<EditorStyleSettings> | null = null;

export function useEditorStyleSettings(): UseEditorStyleSettingsReturn {
  if (!sharedSettings) {
    sharedSettings = ref<EditorStyleSettings>(loadFromStorage());
  }
  const settings = sharedSettings;

  const editorStyle = computed(() => ({
    "--editor-font-size": `${settings.value.fontSize}pt`,
    "--editor-line-height": String(settings.value.lineHeight),
    "--editor-letter-spacing": `${settings.value.letterSpacing}pt`,
  }));

  function loadSettings(): EditorStyleSettings {
    const loaded = loadFromStorage();
    settings.value = loaded;
    return loaded;
  }

  function saveSettings(newSettings: EditorStyleSettings): void {
    const clamped = clampSettings(newSettings);
    settings.value = clamped;
    saveToStorage(clamped);
  }

  function resetToDefault(): void {
    settings.value = { ...DEFAULT_SETTINGS };
    saveToStorage(DEFAULT_SETTINGS);
  }

  return {
    settings,
    editorStyle,
    loadSettings,
    saveSettings,
    resetToDefault,
    defaults: { ...DEFAULT_SETTINGS },
    limits: {
      fontSize: { min: FONT_SIZE_MIN, max: FONT_SIZE_MAX },
      lineHeight: { min: LINE_HEIGHT_MIN, max: LINE_HEIGHT_MAX },
      letterSpacing: { min: LETTER_SPACING_MIN, max: LETTER_SPACING_MAX },
    },
  };
}
